<!--
  FlowFormStep.vue — renderer generico di un nodo di flow.

  È il componente che `compileFlow()` monta per OGNI nodo: riceve la
  definizione del nodo (JSON) e lo scope, e disegna i controlli descritti nei
  `fields`. Nessuna logica di dominio, nessuna navigazione: quelle stanno
  rispettivamente nelle azioni registrate e in `WizardModal.vue`.

  Contratto verso il motore (lo stesso degli step scritti a mano):
    - `v-model`  → la risposta del nodo, SEMPRE un oggetto { nomeCampo: valore }
    - `@advance` → richiesta di avanzamento (rispettata solo se `auto_advance`)

  I controlli non hanno tutti lo stesso contratto v-model (CDatePicker usa
  `date`/`update:date`, CMultiSelect usa `value`/`change`): l'adattamento è
  descritto in `engine/controls.ts` e applicato qui in `bindingsFor()`.
-->
<template>
  <div>
    <p v-if="node.description" class="mb-3">{{ resolvedDescription }}</p>

    <!-- Nodo di riepilogo: sola lettura -->
    <ul v-if="isConfirm" class="list-unstyled mb-0">
      <li v-for="(row, i) in confirmRows" :key="i" class="mb-1">
        <strong>{{ row.label }}:</strong> {{ row.value }}
      </li>
    </ul>

    <!-- Nodo form -->
    <CRow v-else>
      <CCol v-for="field in visibleFields" :key="field.spec.name" :md="field.spec.cols ?? 12" class="mb-3">
        <CFormLabel v-if="field.spec.label && field.control.kind !== 'display'">
          {{ field.label }}
          <span v-if="field.spec.required" class="text-danger">*</span>
        </CFormLabel>

        <!-- Testo informativo -->
        <p v-if="field.control.kind === 'display'" class="mb-0" :class="displayClass(field)">
          {{ field.label }}
        </p>

        <!-- Scelta singola a radio.
             Due accortezze obbligate dal contratto di CFormCheck:
             1. `modelValue` è dichiarato `[Array, Boolean, String]` con Boolean
                PRIMA di String: Vue applica il boolean-casting e trasforma la
                stringa vuota in `true`. Un `''` renderebbe TUTTI i radio come
                `checked` e ammazzerebbe l'emit. Quando nulla è selezionato si
                passa quindi `undefined`, non `''`.
             2. Si ascolta `change` e non `update:modelValue`: `change` viene
                emesso incondizionatamente in testa a handleChange, mentre
                `update:modelValue` passa da un albero di if che dipende dal
                tipo di modelValue. -->
        <template v-else-if="field.control.kind === 'radio-group'">
          <CFormCheck
            v-for="(opt, i) in field.options"
            :key="String(i)"
            type="radio"
            :name="`${node.id}-${field.spec.name}`"
            :id="`${node.id}-${field.spec.name}-${i}`"
            :value="String(i)"
            :label="opt.label"
            :disabled="field.disabled"
            :inline="field.props.inline === true"
            :model-value="selectedIndex(field)"
            @change="setValue(field, opt.value)"
          />
        </template>

        <!-- Scelta con tasti grandi -->
        <div
          v-else-if="field.control.kind === 'button-group'"
          class="flow-button-group gap-3"
          :class="field.props.vertical === true ? 'flow-button-group--vertical' : ''"
        >
          <CButton
            v-for="opt in field.options"
            :key="String(opt.value)"
            class="py-3 text-start"
            :color="isSelected(field, opt) ? (opt.color ?? 'success') : 'secondary'"
            :variant="isSelected(field, opt) ? undefined : 'outline'"
            :disabled="field.disabled"
            @click="setValue(field, opt.value)"
          >
            <div class="fw-semibold">
              <font-awesome-icon v-if="opt.icon" :icon="opt.icon" class="me-2" />{{ opt.label }}
            </div>
            <small v-if="opt.description">{{ opt.description }}</small>
          </CButton>
        </div>

        <!-- Controllo singolo (CFormInput, CDatePicker, CMultiSelect, ...) -->
        <component
          v-else-if="field.control.component"
          :is="field.control.component"
          v-bind="bindingsFor(field)"
        />

        <small v-if="field.hint" class="text-medium-emphasis d-block mt-1">{{ field.hint }}</small>
      </CCol>
    </CRow>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import type { FlowField, FlowNode, FlowOption, FlowScope } from '../engine/flowTypes'
import {
  emptyValueOf, getFlowControl, normalizeOptions, sanitizeControlProps,
  type FlowControlDefinition,
} from '../engine/controls'
import { evaluateCondition } from '../engine/conditions'
import { resolveDeep, resolveTemplate } from '../engine/template'
import { getScopeValue } from '../engine/scope'

const model = defineModel<Record<string, unknown>>()

const props = defineProps<{
  node: FlowNode
  scope: FlowScope
  /**
   * `WizardModal` passa sempre `:ctx`. Dichiararlo evita che finisca come
   * attributo sull'elemento radice; il renderer usa `scope`, non `ctx`.
   */
  ctx?: unknown
}>()

const emit = defineEmits<{ advance: [] }>()

/** Campo risolto: spec originale + tutto ciò che dipende dallo scope. */
interface ResolvedField {
  spec: FlowField
  control: FlowControlDefinition
  label: string
  hint: string
  props: Record<string, unknown>
  options: FlowOption[]
  disabled: boolean
}

const isConfirm = computed(() => props.node.type === 'confirm')

const resolvedDescription = computed(() =>
  String(resolveTemplate(props.node.description ?? '', props.scope)),
)

const confirmRows = computed(() =>
  (props.node.rows ?? []).map((row) => ({
    label: String(resolveTemplate(row.label, props.scope)),
    value: formatConfirmValue(resolveTemplate(row.value, props.scope)),
  })),
)

function formatConfirmValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Sì' : 'No'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
  return String(value)
}

/** Risolve i campi del nodo contro lo scope corrente. */
const resolvedFields = computed<ResolvedField[]>(() =>
  (props.node.fields ?? []).flatMap((spec) => {
    const control = getFlowControl(spec.control)
    if (!control) {
      // Non si interrompe il render: il nodo mostra gli altri campi e il
      // problema è già segnalato da `validateFlow()` in fase di pubblicazione.
      console.warn(`[flow] controllo non registrato '${spec.control}' (campo '${spec.name}')`)
      return []
    }

    const rawOptions = spec.optionsFrom
      ? getScopeValue(props.scope, spec.optionsFrom)
      : spec.options

    const allOptions = resolveDeep(normalizeOptions(rawOptions), props.scope)

    return [{
      spec,
      control,
      label: String(resolveTemplate(spec.label ?? '', props.scope)),
      hint: String(resolveTemplate(spec.hint ?? '', props.scope)),
      props: sanitizeControlProps(control, resolveDeep(spec.props ?? {}, props.scope)),
      // `optionsWhere` si valuta per ogni opzione, con l'opzione sullo scope.
      options: spec.optionsWhere
        ? allOptions.filter((option) =>
            evaluateCondition(spec.optionsWhere, { ...props.scope, option }))
        : allOptions,
      disabled: spec.disabled_when
        ? evaluateCondition(spec.disabled_when, props.scope)
        : false,
    }]
  }),
)

const visibleFields = computed(() =>
  resolvedFields.value.filter(
    (f) => !f.spec.visible_when || evaluateCondition(f.spec.visible_when, props.scope),
  ),
)

function displayClass(field: ResolvedField): string {
  const color = field.props.color
  const strong = field.props.strong === true
  return [typeof color === 'string' ? `text-${color}` : 'text-medium-emphasis', strong ? 'fw-semibold' : '']
    .filter(Boolean)
    .join(' ')
}

function valueOf(name: string): unknown {
  return model.value?.[name]
}

function isSelected(field: ResolvedField, option: FlowOption): boolean {
  return valueOf(field.spec.name) === option.value
}

/**
 * Indice dell'opzione selezionata, come stringa — è il `modelValue` dei
 * CFormCheck del radio group. `undefined` (MAI `''`) quando nulla è
 * selezionato: la stringa vuota verrebbe convertita in `true` dal
 * boolean-casting di Vue, marcando tutti i radio come `checked`.
 */
function selectedIndex(field: ResolvedField): string | undefined {
  const current = valueOf(field.spec.name)
  const i = field.options.findIndex((o) => o.value === current)
  return i === -1 ? undefined : String(i)
}

/** Scrittura immutabile della risposta del nodo. */
function setValue(field: ResolvedField, raw: unknown): void {
  const value = field.control.fromEvent
    ? field.control.fromEvent(raw, field.props)
    : raw

  model.value = { ...(model.value ?? {}), [field.spec.name]: value }

  // L'avanzamento automatico è comunque filtrato dal modale (`autoAdvance`,
  // `validate`, `busy`): qui si limita a segnalare che l'utente ha scelto.
  if (props.node.auto_advance) emit('advance')
}

/**
 * Costruisce le props da passare al componente del controllo, incluso il
 * listener. Si passa `onXxx` via `v-bind` invece di usare `@[evento]`: con
 * nomi come `update:modelValue` è più prevedibile.
 */
function bindingsFor(field: ResolvedField): Record<string, unknown> {
  const control = field.control
  const modelProp = control.modelProp ?? 'modelValue'
  const modelEvent = control.modelEvent ?? 'update:modelValue'
  const handlerKey = `on${modelEvent.charAt(0).toUpperCase()}${modelEvent.slice(1)}`
  const value = valueOf(field.spec.name)

  // La stringa vuota va neutralizzata sui controlli con boolean-casting,
  // altrimenti Vue la converte in `true` (vedi `booleanCast` in controls.ts).
  const safeValue = control.booleanCast && value === '' ? undefined : value

  const bindings: Record<string, unknown> = {
    ...field.props,
    [modelProp]: control.toProp ? control.toProp(safeValue) : safeValue,
    [handlerKey]: (payload: unknown) => setValue(field, payload),
  }

  if (control.optionsProp) {
    // Voce vuota iniziale per i <select>: senza di essa il browser
    // preselezionerebbe la prima opzione senza intervento dell'utente.
    const needsPlaceholder = control.placeholderOption
      && !field.options.some((o) => o.value === '' || o.value === null)
    bindings[control.optionsProp] = needsPlaceholder
      ? [{ value: '', label: String(field.props.placeholder ?? '— seleziona —') }, ...field.options]
      : field.options
  }
  if (field.disabled) bindings.disabled = true

  return bindings
}

/**
 * Semina i valori iniziali. Un campo senza `default` parte dal valore vuoto del
 * suo controllo, così ogni chiave della risposta esiste sempre e le condizioni
 * su `$answer.campo` non incontrano `undefined` a sorpresa.
 *
 * Si osserva `node.id` e non `onMounted`: tutti i nodi di un flow usano QUESTO
 * componente, quindi senza la `:key` in WizardModal l'istanza verrebbe riusata
 * e `onMounted` scatterebbe solo sul primo nodo. La key c'è, ma legare la
 * semina al nodo rende il componente corretto comunque.
 *
 * `flush: 'post'` per non scrivere sul modello durante il render/setup.
 */
watch(() => props.node.id, () => {
  const current = model.value
  const seeded: Record<string, unknown> = { ...(current ?? {}) }
  let changed = false

  for (const field of resolvedFields.value) {
    if (field.control.kind === 'display') continue
    if (seeded[field.spec.name] !== undefined) continue

    seeded[field.spec.name] = field.spec.default !== undefined
      ? resolveTemplate(field.spec.default, props.scope)
      : emptyValueOf(field.control)
    changed = true
  }

  if (changed || current === undefined) model.value = seeded
}, { immediate: true, flush: 'post' })

/**
 * Un `optionsWhere` che dipende da un altro campo può rendere NON PIÙ AMMESSA
 * una risposta già data (si scegle la ricezione, poi si cambia reparto).
 * Tenerla significherebbe salvare un valore che l'utente non vede più a
 * schermo: si azzera.
 */
watch(resolvedFields, (fields) => {
  const current = model.value
  if (!current) return

  let next: Record<string, unknown> | null = null

  for (const field of fields) {
    if (!field.spec.optionsWhere) continue
    const value = current[field.spec.name]
    if (value === undefined || value === null || value === '') continue

    const allowed = field.options.map((o) => o.value)

    if (Array.isArray(value)) {
      const kept = value.filter((v) => allowed.includes(v))
      if (kept.length === value.length) continue
      next ??= { ...current }
      next[field.spec.name] = kept
      continue
    }

    if (allowed.includes(value)) continue
    next ??= { ...current }
    next[field.spec.name] = emptyValueOf(field.control)
  }

  if (next) model.value = next
}, { flush: 'post' })
</script>


<style scoped>
/*
  I tasti grandi si dispongono su una griglia che si adatta allo spazio
  disponibile: tante colonne quante ne stanno con una larghezza minima
  leggibile, poi vanno a capo da sole. `min(100%, ...)` evita l'overflow
  quando il contenitore è più stretto della larghezza minima (telefoni).
  Nessun breakpoint: conta la larghezza del contenitore, non dello schermo.
*/
.flow-button-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 16rem), 1fr));
  align-items: stretch;
}

.flow-button-group--vertical {
  grid-template-columns: 1fr;
}
</style>
